import { useState } from 'react';
import { EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable newsletter subscription form.
 *
 * Props:
 *  - variant: 'dark' (for footer / dark hero) | 'light' (for light sections)
 *  - source:  'footer' | 'knowledge' | 'other' — sent to backend for analytics
 *  - title, subtitle: optional headings above the form
 *  - compact: boolean — if true, hides the heading block (form only)
 */
const NewsletterForm = ({
  variant = 'light',
  source = 'footer',
  title = 'اشترك في النشرة البريدية',
  subtitle = 'مقالات مختارة، أدوات مهنية، ومستجدات مركز المعرفة — مباشرة إلى بريدك.',
  compact = false,
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | already
  const isDark = variant === 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('يرجى إدخال بريدك الإلكتروني');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, source }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'تعذّر إتمام الاشتراك');
      if (data.status === 'already_subscribed') {
        setStatus('already');
        toast.info('هذا البريد الإلكتروني مسجل مسبقًا في النشرة البريدية.');
      } else {
        setStatus('success');
        toast.success('تم تسجيل اشتراكك بنجاح في النشرة البريدية.');
      }
      setEmail('');
    } catch (err) {
      setStatus('idle');
      toast.error(err.message || 'حدث خطأ، يرجى المحاولة لاحقًا');
    }
  };

  const done = status === 'success' || status === 'already';

  const inputCls = isDark
    ? 'flex-1 bg-[#243B19] border border-[#3B5626] text-white placeholder-[#7E916A] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#8FAF63]'
    : 'flex-1 bg-white border border-[#D8D2C8] text-[#1F3D2B] placeholder-[#92A082] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#4A641F]';

  const btnCls = isDark
    ? 'bg-[#4A641F] hover:bg-[#3B5626] text-white rounded-xl px-5 py-3 text-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2'
    : 'bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-5 py-3 text-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2';

  const headingCls = isDark ? 'text-white' : 'text-[#1F3D2B]';
  const subtitleCls = isDark ? 'text-[#B9C2AB]' : 'text-[#6B7565]';

  return (
    <div data-testid={`newsletter-form-${source}`} dir="rtl">
      {!compact && (
        <div className="mb-4">
          <h4
            className={`text-base sm:text-lg font-bold ${headingCls} font-['IBM_Plex_Sans_Arabic'] mb-1.5 flex items-center gap-2`}
          >
            <EnvelopeSimple size={20} weight="duotone" />
            {title}
          </h4>
          <p className={`text-xs sm:text-sm ${subtitleCls} leading-7`}>
            {subtitle}
          </p>
        </div>
      )}

      {done ? (
        <div
          data-testid="newsletter-success"
          className={`flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm ${
            isDark
              ? 'bg-[#243B19] border border-[#4A641F] text-[#D8E0C9]'
              : 'bg-[#F5F2EC] border border-[#4A641F] text-[#1F3D2B]'
          }`}
        >
          <CheckCircle size={18} weight="fill" className="text-[#4A641F]" />
          <span>
            {status === 'already'
              ? 'هذا البريد الإلكتروني مسجل مسبقًا في النشرة البريدية.'
              : 'تم تسجيل اشتراكك بنجاح — تحقق من بريدك للترحيب.'}
          </span>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-2.5"
          noValidate
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="بريدك الإلكتروني"
            aria-label="بريدك الإلكتروني"
            dir="ltr"
            required
            autoComplete="email"
            data-testid="newsletter-email-input"
            className={inputCls}
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            data-testid="newsletter-submit-btn"
            className={btnCls}
          >
            {status === 'loading' ? 'جاري…' : 'اشترك'}
          </button>
        </form>
      )}
    </div>
  );
};

export default NewsletterForm;
